/**
 * Pieces of the CR learning tree review screen (INT-221, INT-225), styled after
 * the proposal's prototype: the CentralReach tree row with its role chip and
 * menu, the Needs confirm card, the goals-and-targets preview, the checks panel
 * and the legend. Pure presentation; the screen owns the state.
 */
import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { LockClosedIcon } from "@heroicons/react/24/solid";
import React from "react";

import { Badge, Button } from "@fronterahealth/frontera-ui-components";

import {
  CheckLine,
  CrLevel,
  PreviewGoal,
  ROLE_LABEL,
  ReviewNode,
  TreeRole,
  dataTypeText,
  fronteraTypeText,
  goalOf,
  isFrozen,
  notImportedReason,
  recordsData,
} from "@pages/ClinicalReportDetails/LearningTreeReview/learningTreeCascade";

const CHIP_CLASS: Record<TreeRole, string> = {
  GOAL: "bg-[#e5fbff] text-[#05586d] border-[#b1e4e3]",
  TARGET: "bg-white text-text-secondary border-limestone-400",
  SKIP_ITEM: "bg-transparent text-limestone-800 border-limestone-400 border-dashed",
  SKIP_BRANCH: "bg-limestone-200 text-limestone-800 border-transparent",
};

/** What CentralReach calls the item (from FDM), separate from its Frontera role. */
const CR_TAG: Record<CrLevel, { text: string; title: string; className: string }> = {
  FOLDER: {
    text: "FOLDER",
    title: "Folder",
    className: "bg-white text-limestone-800 border-limestone-500 border-dashed",
  },
  LTG: { text: "LTG", title: "Long-term goal", className: "bg-[#f1dcff] text-[#62286e] border-[#e0b8f0]" },
  STG: { text: "STG", title: "Short-term goal", className: "bg-[#dbeafe] text-[#1e40af] border-[#bfdbfe]" },
  TARGET: { text: "TARGET", title: "Target", className: "bg-[#fff3c4] text-[#6e4c00] border-[#ffe58f]" },
};

export const CrTag: React.FC<{ level: CrLevel }> = ({ level }) => {
  const tag = CR_TAG[level];
  return (
    <span
      data-cr-level={level}
      title={tag.title}
      className={`inline-flex h-[18px] shrink-0 items-center rounded border px-1.5 text-[10px] font-bold tracking-wide ${tag.className}`}
    >
      {tag.text}
    </span>
  );
};

// Approved in the report: its role color, faded; nothing to click (not even Unlock).
const BLOCKED_CHIP_CLASS = "opacity-60 cursor-not-allowed";

export const RoleChip: React.FC<{ role: TreeRole | null; locked?: boolean; blocked?: boolean }> = ({
  role,
  locked,
  blocked,
}) => {
  if (!role) return <span className="text-xs text-text-tertiary">No role</span>;
  return (
    <span
      data-blocked={blocked || undefined}
      className={`inline-flex items-center gap-1.5 rounded-md border px-1.5 py-0.5 text-[11px] font-medium ${
        CHIP_CLASS[role]
      } ${blocked ? BLOCKED_CHIP_CLASS : ""}`}
    >
      {locked && <LockClosedIcon className="h-3 w-3" />}
      {role === "GOAL" && !locked ? "◆ " : ""}
      {ROLE_LABEL[role]}
    </span>
  );
};

/** Approved only through its goal: blocked like its goal, but not approved itself. */
const goalApprovedOnly = (node: ReviewNode): boolean =>
  node.approvedIn.length > 0 && node.approvedIn.every((r) => node.goalApprovedIn.includes(r));

/** Hover text for an approved item, or one blocked by its approved goal. */
const approvalTitle = (node: ReviewNode): string =>
  `${goalApprovedOnly(node) ? "Goal approved" : "Approved"} in ${node.approvedIn.join(", ")}`;

/** Who set the role, or that the item is already in a report, shown left of the chip. */
const SourceLabel: React.FC<{ node: ReviewNode }> = ({ node }) => {
  let text = "Suggested";
  let className = "text-limestone-700";
  let title: string | undefined;
  // "Approved" only on items approved themselves; a target blocked by its goal
  // says so on hover.
  const approved = node.approvedIn.length > 0 && !goalApprovedOnly(node);
  // Locked rows show only the lock on their chip; the spacer keeps chips aligned.
  if (node.locked && !approved) return <span className="w-[68px]" />;
  if (approved) {
    text = "Approved";
    className = "text-[#6e4c00]";
    title = approvalTitle(node);
  } else if (node.roleSource === "PERSON") {
    text = "✓ You";
    className = "text-green-700 font-medium";
  } else if (node.roleSource === "FOLLOWS") {
    text = "Follows";
    className = "text-[#3730a3]";
  } else if (node.usedIn.length) {
    text = "In report";
    className = "text-limestone-800";
    title = `In ${node.usedIn.join(", ")}`;
  } else if (node.roleSource === "IMPORTED") {
    text = "Imported";
    className = "text-limestone-800";
    title = "Imported before; not in this report";
  }
  if (goalApprovedOnly(node)) title = approvalTitle(node);
  return (
    <span className={`w-[68px] text-right text-[11px] ${className}`} title={title}>
      {text}
    </span>
  );
};

export interface MenuOption {
  role: TreeRole;
  disabled: boolean;
  reason: string | null;
}

const OPTION_ICON: Record<TreeRole, string> = { GOAL: "◆", TARGET: "•", SKIP_ITEM: "▢", SKIP_BRANCH: "⊘" };

const optionDescription = (nodes: ReviewNode[], node: ReviewNode, option: MenuOption): string => {
  if (option.reason) return option.reason;
  switch (option.role) {
    case "GOAL":
      return recordsData(node)
        ? "Goal with data recorded on the goal itself, or on targets under it."
        : "Data items under it become its targets, down to the next goal.";
    case "TARGET": {
      const goal = goalOf(nodes, node.externalId);
      const goalName = goal ? nodes.find((n) => n.externalId === goal)?.name : null;
      return goalName
        ? `Imported under “${goalName}”, the nearest goal above it.`
        : "No goal above it yet. Set an item above it to Goal before you lock.";
    }
    case "SKIP_ITEM":
      return "Skip only this item. Items under it keep their roles.";
    default:
      return "Skip this item and everything under it. Nothing here is imported.";
  }
};

interface RoleMenuProps {
  nodes: ReviewNode[];
  node: ReviewNode;
  options: MenuOption[];
  onPick: (value: TreeRole | "RESET") => void;
  onClose: () => void;
}

const RoleMenu: React.FC<RoleMenuProps> = ({ nodes, node, options, onPick, onClose }) => (
  <>
    <button type="button" aria-label="Close menu" className="fixed inset-0 z-20 cursor-default" onClick={onClose} />
    <div
      role="menu"
      aria-label={`Roles for ${node.name}`}
      className="absolute right-3 top-[38px] z-30 w-[300px] rounded-lg border border-limestone-300 bg-white p-1.5 shadow-[0_10px_25px_rgba(14,37,40,0.12)]"
    >
      {options.map((option) => {
        const current = option.role === node.role;
        return (
          <button
            key={option.role}
            type="button"
            role="menuitem"
            disabled={option.disabled || current}
            onClick={() => onPick(option.role)}
            className="flex w-full gap-2.5 rounded-md px-2.5 py-2 text-left enabled:hover:bg-limestone-100 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <span aria-hidden className="w-[18px] text-center text-[#24727f]">
              {current ? "✓" : OPTION_ICON[option.role]}
            </span>
            <span>
              <span className="block text-sm font-medium text-text-primary">{ROLE_LABEL[option.role]}</span>
              <span className="mt-0.5 block text-xs leading-4 text-text-tertiary">
                {optionDescription(nodes, node, option)}
              </span>
            </span>
          </button>
        );
      })}
      <div className="my-1 h-px bg-limestone-200" />
      <button
        type="button"
        role="menuitem"
        onClick={() => onPick("RESET")}
        className="flex w-full gap-2.5 rounded-md px-2.5 py-2 text-left hover:bg-limestone-100"
      >
        <span aria-hidden className="w-[18px] text-center text-[#24727f]">
          ↺
        </span>
        <span>
          <span className="block text-sm font-medium text-text-primary">Reset branch to our suggestion</span>
          <span className="mt-0.5 block text-xs leading-4 text-text-tertiary">
            This item and everything under it, except locked and approved items
          </span>
        </span>
      </button>
    </div>
  </>
);

const LockedMenu: React.FC<{ node: ReviewNode; onUnlock: () => void; onClose: () => void }> = ({
  node,
  onUnlock,
  onClose,
}) => (
  <>
    <button type="button" aria-label="Close menu" className="fixed inset-0 z-20 cursor-default" onClick={onClose} />
    <div
      role="menu"
      aria-label={`Locked ${node.name}`}
      className="absolute right-3 top-[38px] z-30 w-[300px] rounded-lg border border-limestone-300 bg-white p-1.5 shadow-[0_10px_25px_rgba(14,37,40,0.12)]"
    >
      <button
        type="button"
        role="menuitem"
        onClick={onUnlock}
        className="flex w-full gap-2.5 rounded-md px-2.5 py-2 text-left hover:bg-limestone-100"
      >
        <span aria-hidden className="w-[18px] text-center text-[#24727f]">
          🔓
        </span>
        <span>
          <span className="block text-sm font-medium text-text-primary">Unlock this branch</span>
          <span className="mt-0.5 block text-xs leading-4 text-text-tertiary">
            Unlock it and everything under it to change roles. Nothing changes until you lock again.
          </span>
        </span>
      </button>
    </div>
  </>
);

export interface TreeRowProps {
  nodes: ReviewNode[];
  node: ReviewNode;
  depth: number;
  hasChildren: boolean;
  open: boolean;
  highlighted: boolean;
  menuOpen: boolean;
  options: MenuOption[];
  onToggle: () => void;
  onMenu: (open: boolean) => void;
  onPick: (value: TreeRole | "RESET") => void;
  onUnlock: () => void;
  /** Hovering a row highlights it (and what's under it) on the right. */
  onHover: (hovering: boolean) => void;
}

const nameClassOf = (node: ReviewNode): string => {
  if (node.role === "SKIP_BRANCH" || node.removed) return "text-limestone-700 line-through";
  return node.role === "SKIP_ITEM" ? "text-text-secondary" : "text-text-primary";
};

/** The row's role control: approved (no menu), locked (Unlock menu) or the role dropdown. */
const RowChip: React.FC<{ node: ReviewNode; menuOpen: boolean; onMenu: (open: boolean) => void }> = ({
  node,
  menuOpen,
  onMenu,
}) => {
  // Approved in this report: no menu, not even Unlock, until it's un-approved there.
  if (node.approvedIn.length) {
    return (
      <span title={approvalTitle(node)}>
        <RoleChip role={node.role} locked={node.locked} blocked />
      </span>
    );
  }
  if (node.locked) {
    return (
      <button type="button" aria-label={`Locked ${node.name}`} onClick={() => onMenu(!menuOpen)}>
        <RoleChip role={node.role} locked />
      </button>
    );
  }
  return (
    <button
      type="button"
      aria-label={`Role for ${node.name}`}
      aria-haspopup="menu"
      onClick={() => onMenu(!menuOpen)}
      className={`inline-flex min-w-[84px] items-center justify-between gap-1.5 rounded-md border px-1.5 py-0.5 text-[11px] font-medium ${
        node.role ? CHIP_CLASS[node.role] : CHIP_CLASS.SKIP_BRANCH
      }`}
    >
      {node.role === "GOAL" ? "◆ " : ""}
      {node.role ? ROLE_LABEL[node.role] : "No role"}
      <span className="text-[9px] opacity-70">▼</span>
    </button>
  );
};

/** The row's data type, status badges, the rule's hint and why it's skipped. */
const RowNotes: React.FC<{ nodes: ReviewNode[]; node: ReviewNode }> = ({ nodes, node }) => {
  const reason = notImportedReason(nodes, node.externalId);
  const ruleHint =
    node.roleSource === "IMPORTED" && !node.locked && node.ruleRole && node.ruleRole !== node.role
      ? `Suggested: ${ROLE_LABEL[node.ruleRole]}`
      : null;
  return (
    <>
      {node.dataType && (
        <span className="whitespace-nowrap text-xs text-limestone-800" title={node.dataType}>
          {dataTypeText(node)}
        </span>
      )}
      {node.confirmKind && !node.confirmAnswered && !node.locked && (
        <Badge text="Needs confirm" appearance="warn" dot />
      )}
      {node.status === "WAITING" && <Badge text="New" appearance="ai" />}
      {node.removed && <Badge text="Removed in CR" appearance="error" />}
      {node.movedInCr && !node.removed && <Badge text="Moved in CR" appearance="warn" />}
      {node.isBehavior && <Badge text="Behavior" appearance="neutral" />}
      {ruleHint && <span className="whitespace-nowrap text-xs text-text-tertiary">{ruleHint}</span>}
      {reason && (
        <span className="min-w-0 truncate text-xs text-text-tertiary" title={reason}>
          {reason}
        </span>
      )}
    </>
  );
};

export const TreeRow: React.FC<TreeRowProps> = ({
  nodes,
  node,
  depth,
  hasChildren,
  open,
  highlighted,
  menuOpen,
  options,
  onToggle,
  onMenu,
  onPick,
  onUnlock,
  onHover,
}) => {
  const frozen = isFrozen(node);
  return (
    <div
      id={`tree-node-${node.externalId}`}
      className={`relative flex min-h-[40px] items-center gap-1.5 py-1.5 pr-4 hover:bg-limestone-100 ${
        highlighted ? "bg-limestone-100" : ""
      }`}
      style={{ paddingLeft: 12 + depth * 22 }}
      onMouseEnter={() => onHover(true)}
      onMouseLeave={() => onHover(false)}
    >
      <div className="flex min-w-0 flex-1 items-center gap-1.5">
        <button
          type="button"
          aria-label={open ? `Collapse ${node.name}` : `Expand ${node.name}`}
          className="flex h-6 w-[18px] shrink-0 items-center justify-center text-text-secondary"
          onClick={onToggle}
          disabled={!hasChildren}
        >
          {hasChildren &&
            (open ? <ChevronDownIcon className="h-3.5 w-3.5" /> : <ChevronRightIcon className="h-3.5 w-3.5" />)}
        </button>
        {node.crLevel && <CrTag level={node.crLevel} />}
        <span className={`truncate text-sm ${nameClassOf(node)}`} title={node.name}>
          {node.name}
        </span>
        <RowNotes nodes={nodes} node={node} />
      </div>
      <div className="flex shrink-0 items-center gap-1.5">
        {!node.removed && <SourceLabel node={node} />}
        {!node.removed && <RowChip node={node} menuOpen={menuOpen} onMenu={onMenu} />}
      </div>
      {menuOpen && !frozen && (
        <RoleMenu nodes={nodes} node={node} options={options} onPick={onPick} onClose={() => onMenu(false)} />
      )}
      {menuOpen && node.locked && !node.approvedIn.length && (
        <LockedMenu node={node} onUnlock={onUnlock} onClose={() => onMenu(false)} />
      )}
    </div>
  );
};

export interface ConfirmQuestion {
  question: string;
  detail: string;
  keep: { title: string; preview: string };
  other: { title: string; preview: string; role: TreeRole };
}

export const ConfirmCard: React.FC<{
  question: ConfirmQuestion;
  choice: "keep" | "other";
  onChoice: (choice: "keep" | "other") => void;
  onConfirm: () => void;
}> = ({ question, choice, onChoice, onConfirm }) => {
  const option = (key: "keep" | "other", title: string, preview: string) => (
    <label
      className={`mt-2.5 flex cursor-pointer gap-2.5 rounded-md border bg-white px-3 py-2.5 ${
        choice === key ? "border-[#24727f] shadow-[0_0_0_1px_#24727f]" : "border-limestone-300"
      }`}
    >
      <input
        type="radio"
        className="mt-0.5 accent-[#24727f]"
        checked={choice === key}
        onChange={() => onChoice(key)}
        aria-label={title}
      />
      <span>
        <span className="block text-sm font-medium text-text-primary">{title}</span>
        <span className="mt-1 block text-xs text-text-tertiary">{preview}</span>
      </span>
    </label>
  );
  return (
    <div className="mx-4 mb-2.5 mt-1 rounded-lg border border-[#f9b72a] bg-[#fffbeb] px-4 py-3.5">
      <div className="text-sm font-semibold text-[#332200]">{question.question}</div>
      <div className="mt-1 text-[13px] text-[#6e4c00]">{question.detail}</div>
      {option("keep", question.keep.title, question.keep.preview)}
      {option("other", question.other.title, question.other.preview)}
      <div className="mt-3 flex justify-end">
        <Button text="Confirm" variant="primary" size="sm" onClick={onConfirm} />
      </div>
    </div>
  );
};

/** CR's name for the data type on hover, only when the preview shows Frontera's instead. */
const crNameOnHover = (node: ReviewNode): string | undefined =>
  node.progressType ? dataTypeText(node) || undefined : undefined;

/** A preview goal's count: its targets, or the data recorded on the goal itself. */
const goalSummary = (goal: ReviewNode, targetCount: number): string => {
  if (targetCount === 0 && recordsData(goal)) return `data on goal · ${fronteraTypeText(goal)}`;
  return targetCount === 1 ? "1 target" : `${targetCount} targets`;
};

export const Preview: React.FC<{
  goals: PreviewGoal[];
  retired: ReviewNode[];
  skipped: number;
  importedNotInTree: { externalId: string; name?: string | null; role: string }[];
  /** Items hovered on the left, highlighted here. */
  highlighted?: Set<string>;
}> = ({ goals, retired, skipped, importedNotInTree, highlighted }) => (
  <div className="pb-3 pt-1">
    {goals.map(({ goal, targets }) => (
      <React.Fragment key={goal.externalId}>
        <div
          data-preview-id={goal.externalId}
          className={`flex min-h-[36px] items-center gap-2 px-4 py-1.5 ${
            highlighted?.has(goal.externalId) ? "bg-[#e5fbff]" : ""
          }`}
        >
          <span className="mx-1 h-2 w-2 shrink-0 rotate-45 bg-[#24727f]" />
          <span className="min-w-0 flex-1 truncate text-sm font-medium text-text-primary">{goal.name}</span>
          <span
            className="text-xs text-limestone-800"
            title={targets.length === 0 && recordsData(goal) ? crNameOnHover(goal) : undefined}
          >
            {goalSummary(goal, targets.length)}
          </span>
          {goal.status === "WAITING" && <Badge text="Waiting" appearance="warn" />}
        </div>
        {targets.map((t) => (
          <div
            key={t.externalId}
            data-preview-id={t.externalId}
            className={`flex min-h-[30px] items-center gap-2 py-1 pl-10 pr-4 ${
              highlighted?.has(t.externalId) ? "bg-[#e5fbff]" : ""
            }`}
          >
            <span className="h-[5px] w-[5px] shrink-0 rounded-full bg-limestone-700" />
            <span className="flex-1 text-sm text-text-secondary">{t.name}</span>
            <span className="text-xs text-limestone-800" title={crNameOnHover(t)}>
              {fronteraTypeText(t)}
            </span>
            {t.status === "WAITING" && <Badge text="Waiting" appearance="warn" />}
          </div>
        ))}
      </React.Fragment>
    ))}
    {goals.length === 0 && <div className="px-4 py-6 text-[13px] text-text-tertiary">Nothing will be imported.</div>}
    {retired.length > 0 && (
      <>
        <div className="px-4 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wider text-limestone-800">
          Will be retired
        </div>
        {retired.map((n) => (
          <div key={n.externalId} className="flex justify-between gap-2 px-4 py-1.5 text-[13px] text-[#942e2e]">
            <span>{n.name}</span>
            <span>removed in CentralReach</span>
          </div>
        ))}
      </>
    )}
    {importedNotInTree.length > 0 && (
      <>
        <div className="px-4 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wider text-limestone-800">
          Imported, not in CentralReach anymore
        </div>
        {importedNotInTree.map((item) => (
          <div
            key={`${item.role}-${item.externalId}`}
            className="flex justify-between gap-2 px-4 py-1.5 text-[13px] text-text-tertiary"
          >
            <span>{item.name}</span>
            <span>{item.role === "GOAL" ? "Goal" : "Target"} · left as is</span>
          </div>
        ))}
      </>
    )}
    <div className="mt-2 flex justify-between gap-2 border-t border-limestone-200 px-4 pb-1.5 pt-2.5 text-[13px] text-text-tertiary">
      <span>Skipped items</span>
      <span>{skipped} · no requests</span>
    </div>
  </div>
);

export const ChecksPanel: React.FC<{
  lines: CheckLine[];
  nameOf: (id: string) => string;
  onShow: (id: string) => void;
  onMakeFolder: (id: string) => void;
}> = ({ lines, nameOf, onShow, onMakeFolder }) => {
  const errors = lines.filter((l) => l.level === "error");
  const warnings = lines.filter((l) => l.level === "warning");
  return (
    <div className="mt-4 rounded-lg border border-limestone-200 bg-white">
      <div className="flex items-center justify-between border-b border-limestone-200 px-4 py-2.5">
        <span className="text-sm font-semibold text-text-primary">Before you lock</span>
        <span className="flex gap-2">
          {errors.length > 0 && <Badge text={`${errors.length} to fix`} appearance="error" />}
          {warnings.length > 0 && (
            <Badge text={`${warnings.length} warning${warnings.length > 1 ? "s" : ""}`} appearance="warn" />
          )}
        </span>
      </div>
      {lines.length === 0 && (
        <div className="flex items-center gap-2 px-4 py-1.5 text-[13px] text-green-700">
          ✓ Every target has a goal and nothing needs an answer.
        </div>
      )}
      {[...errors, ...warnings].map((l) => (
        <div
          key={`${l.externalId}-${l.code}`}
          className={`flex items-center gap-2 px-4 py-1.5 text-[13px] ${
            l.level === "error" ? "text-[#942e2e]" : "text-[#6e4c00]"
          }`}
        >
          {l.level === "error" ? "✕" : "⚠"}
          <span>
            {nameOf(l.externalId)}: {l.message}
          </span>
          <Button
            text="Show"
            appearance="text"
            style={{ height: "auto", padding: 0 }}
            onClick={() => onShow(l.externalId)}
          />
          {l.code === "EMPTY_GOAL" && (
            <Button
              text="Skip it"
              appearance="text"
              style={{ height: "auto", padding: 0 }}
              onClick={() => onMakeFolder(l.externalId)}
            />
          )}
        </div>
      ))}
    </div>
  );
};

// What each Frontera role does, for the legend.
const ROLE_MEANING: Record<TreeRole, string> = {
  GOAL: "short-term goal",
  TARGET: "target",
  SKIP_ITEM: "only this item skipped",
  SKIP_BRANCH: "item and everything under it skipped",
};

/** The CR tags, Frontera roles and how targets find their goal; kept in view in the sticky footer. */
export const Legend: React.FC = () => (
  <div className="flex flex-col gap-2.5 text-xs text-text-tertiary">
    <div className="flex flex-wrap items-center gap-3.5">
      <span>In CentralReach:</span>
      <CrTag level="LTG" />
      <span>long-term goal</span>
      <CrTag level="STG" />
      <span>short-term goal</span>
      <CrTag level="TARGET" />
      <span>target</span>
      <CrTag level="FOLDER" />
      <span>folder</span>
    </div>
    <div className="flex flex-wrap items-center gap-3.5">
      <span>Frontera role:</span>
      {(["GOAL", "TARGET", "SKIP_ITEM", "SKIP_BRANCH"] as TreeRole[]).map((r) => (
        <span key={r} className="flex items-center gap-1.5">
          <RoleChip role={r} />
          <span>{ROLE_MEANING[r]}</span>
        </span>
      ))}
    </div>
    <div>Only items that record data can be targets. Setting a goal never changes rows you set yourself.</div>
  </div>
);
